import type { ObjectRef } from './workbench-focus';
import type { WorkbenchEvidenceStateMetadata } from './workbench-evidence-state';

export type WorkbenchActionExecutionStatus = 'pending' | 'succeeded' | 'failed' | 'cancelled';

/** P5-07 — downstream spawn continuity metadata (flow · task · document · thread). */
export type WorkbenchDownstreamSpawnKind = 'flow' | 'task' | 'document' | 'thread';

export interface WorkbenchActionExecutionEntry {
  id: string;
  tenantId: string;
  objectId: string;
  businessDomain: string;
  objectLabel?: string;
  actionId: string;
  actionLabel: string;
  handlerRef: string;
  executedAt: string;
  status: WorkbenchActionExecutionStatus;
  undoable: boolean;
  revisionSnapshotId?: string;
  recoverabilityTier?: 'low' | 'medium' | 'high' | 'finance';
  timelineEventId?: string;
  spawnKind?: WorkbenchDownstreamSpawnKind;
  flowRef?: string;
  taskRef?: string;
  documentRef?: string;
  /** OD4-A — kernel action evidence validation metadata for auxiliary refresh. */
  evidenceState?: WorkbenchEvidenceStateMetadata;
}

export interface AppendWorkbenchActionExecutionInput {
  tenantId: string;
  objectRef: ObjectRef;
  actionId: string;
  actionLabel: string;
  handlerRef: string;
  status?: WorkbenchActionExecutionStatus;
  undoable?: boolean;
  revisionSnapshotId?: string;
  recoverabilityTier?: 'low' | 'medium' | 'high' | 'finance';
  timelineEventId?: string;
  spawnKind?: WorkbenchDownstreamSpawnKind;
  flowRef?: string;
  taskRef?: string;
  documentRef?: string;
  /** OD4-A — kernel action evidence validation metadata for auxiliary refresh. */
  evidenceState?: WorkbenchEvidenceStateMetadata;
}

const MAX_ENTRIES_PER_SCOPE = 50;

type ScopeKey = string;

function scopeKey(tenantId: string, objectId: string | undefined): ScopeKey {
  return `${tenantId}:${objectId ?? '__domain__'}`;
}

function createEntryId(): string {
  return `wbx-act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const executionLog = new Map<ScopeKey, WorkbenchActionExecutionEntry[]>();

function appendToScope(key: ScopeKey, entry: WorkbenchActionExecutionEntry): void {
  const current = executionLog.get(key) ?? [];
  const next = [entry, ...current].slice(0, MAX_ENTRIES_PER_SCOPE);
  executionLog.set(key, next);
}

export function appendWorkbenchActionExecution(
  input: AppendWorkbenchActionExecutionInput
): WorkbenchActionExecutionEntry {
  const entry: WorkbenchActionExecutionEntry = {
    id: createEntryId(),
    tenantId: input.tenantId,
    objectId: input.objectRef.id,
    businessDomain: input.objectRef.businessDomain,
    ...(input.objectRef.label ? { objectLabel: input.objectRef.label } : {}),
    actionId: input.actionId,
    actionLabel: input.actionLabel,
    handlerRef: input.handlerRef,
    executedAt: new Date().toISOString(),
    status: input.status ?? 'pending',
    undoable: input.undoable ?? false,
    ...(input.revisionSnapshotId ? { revisionSnapshotId: input.revisionSnapshotId } : {}),
    ...(input.recoverabilityTier ? { recoverabilityTier: input.recoverabilityTier } : {}),
    ...(input.timelineEventId ? { timelineEventId: input.timelineEventId } : {}),
    ...(input.spawnKind ? { spawnKind: input.spawnKind } : {}),
    ...(input.flowRef ? { flowRef: input.flowRef } : {}),
    ...(input.taskRef ? { taskRef: input.taskRef } : {}),
    ...(input.documentRef ? { documentRef: input.documentRef } : {}),
    ...(input.evidenceState ? { evidenceState: input.evidenceState } : {}),
  };

  appendToScope(scopeKey(input.tenantId, input.objectRef.id), entry);
  appendToScope(scopeKey(input.tenantId, undefined), entry);
  return entry;
}

export function listWorkbenchActionExecutions(input: {
  tenantId: string;
  objectId?: string;
  limit?: number;
}): readonly WorkbenchActionExecutionEntry[] {
  const key = scopeKey(input.tenantId, input.objectId?.trim() || undefined);
  const limit = input.limit ?? MAX_ENTRIES_PER_SCOPE;
  return (executionLog.get(key) ?? []).slice(0, limit);
}

export function getLatestWorkbenchActionExecution(input: {
  tenantId: string;
  objectId?: string;
}): WorkbenchActionExecutionEntry | undefined {
  return listWorkbenchActionExecutions({ ...input, limit: 1 })[0];
}

export function patchWorkbenchActionExecution(input: {
  entryId: string;
  timelineEventId?: string;
  clearTimelineEventId?: boolean;
  status?: WorkbenchActionExecutionStatus;
  evidenceState?: WorkbenchEvidenceStateMetadata;
}): WorkbenchActionExecutionEntry | undefined {
  for (const [key, entries] of executionLog.entries()) {
    const index = entries.findIndex((row) => row.id === input.entryId);
    if (index < 0) {
      continue;
    }
    const current = entries[index];
    if (!current) {
      continue;
    }
    const patched: WorkbenchActionExecutionEntry = { ...current };
    if (input.clearTimelineEventId) {
      delete patched.timelineEventId;
    } else if (input.timelineEventId) {
      patched.timelineEventId = input.timelineEventId;
    }
    if (input.status) {
      patched.status = input.status;
    }
    if (input.evidenceState) {
      patched.evidenceState = input.evidenceState;
    }
    const next = [...entries];
    next[index] = patched;
    executionLog.set(key, next);
    return patched;
  }
  return undefined;
}

/** Test-only reset */
export function clearWorkbenchActionExecutionLog(): void {
  executionLog.clear();
}
